import clsx from 'clsx'
import { type default as React, useCallback, useRef, useState, type FunctionComponent } from 'react'
import { scrollBy } from 'simple-smooth-scroll-by'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import { useIntersectionObserver } from '../../utils/useIntersectionObserver'
import style from './CardSwiper.module.sass'
import { SwiperButton } from './SwiperButton'

type CardSwiperProps = {
	isHidden?: boolean
	children: React.ReactNode
}

export const CardSwiper: FunctionComponent<CardSwiperProps> = ({ children, isHidden }) => {
	const carouselRef = useRef<HTMLDivElement>(null)

	const [startCatchRef, setStartCatchRef] = useState<null | HTMLDivElement>(null)
	const [endCatchRef, setEndCatchRef] = useState<null | HTMLDivElement>(null)
	const [isStartVisible, setIsStartVisible] = useState(true)
	const [isEndVisible, setIsEndVisible] = useState(true)

	useIntersectionObserver(
		startCatchRef,
		useCallback((entry) => setIsStartVisible(entry.isIntersecting), []),
	)
	useIntersectionObserver(
		endCatchRef,
		useCallback((entry) => setIsEndVisible(entry.isIntersecting), []),
	)
	const { width } = useWindowDimensions()

	const CAROUSEL_SCROLL_BY_DISTANCE = width && width > 1150 ? 3 * 210 : 210 // 210px is width of a card

	const scrollToPrevious = useCallback(() => {
		if (carouselRef.current) {
			scrollBy(carouselRef.current, 0, -CAROUSEL_SCROLL_BY_DISTANCE)
		}
	}, [CAROUSEL_SCROLL_BY_DISTANCE])

	const scrollToNext = useCallback(() => {
		if (carouselRef.current) {
			scrollBy(carouselRef.current, 0, CAROUSEL_SCROLL_BY_DISTANCE)
		}
	}, [CAROUSEL_SCROLL_BY_DISTANCE])

	return (
		<div className={clsx(style.container)}>
			<div className={clsx(style.items, isHidden && style.itemsIsHidden)}>
				<div className={style.itemsCarousel} ref={carouselRef}>
					<div className={style.catch} ref={setStartCatchRef} />
					{children}
					<div className={style.Catch} ref={setEndCatchRef} />
				</div>
			</div>
			<div className={style.Controls}>
				<div className={clsx(style.ControlsPrevious, isStartVisible && style.ControlsHidden)}>
					<SwiperButton onClick={scrollToPrevious} direction="previous" />
				</div>
				<div className={clsx(style.ControlsNext, isEndVisible && style.ControlsHidden)}>
					<SwiperButton onClick={scrollToNext} direction="next" />
				</div>
			</div>
		</div>
	)
}
